/**
 * LookupAdminTabs — type-tab bar for the lookup admin page (Block 6.C.1).
 *
 * Phase 1.10 (r2 disposition #4) — 5 tabs: subcategories / specialties /
 * Features (rendered label; URL `?type=highlights` preserved for
 * backwards-compat) / Delivery method (URL `?type=service-model`) /
 * service-areas. Active tab
 * driven by `?type=` URL search param (default subcategories). Per
 * ADR-017 F2 (single page, not deeply nested) + ADR-024 D5 (mobile-first;
 * tap target ≥ 44×44px).
 *
 * Categories pre-seeded canonical 9 per ADR-020 §Decision 2; not editable
 * via this admin path so no tab.
 */

import type { ReactNode } from 'react';
import { type LookupTypeUrl, LOOKUP_TYPE_URL_TO_LABEL } from '@/lib/types';
import { TAB_RAIL_CLASSES } from '@/components/primitives/tabRail';

export interface LookupAdminTabsProps {
  activeType: LookupTypeUrl;
  onChange: (type: LookupTypeUrl) => void;
}

const TAB_ORDER: ReadonlyArray<LookupTypeUrl> = [
  'subcategories',
  'specialties',
  'highlights',
  'service-model',
  'service-areas',
];

export function LookupAdminTabs({ activeType, onChange }: LookupAdminTabsProps): ReactNode {
  return (
    <div role="tablist" aria-label="Lookup type" className={`flex gap-1 mb-3 ${TAB_RAIL_CLASSES.tablist}`}>
      {TAB_ORDER.map((type) => {
        const isActive = type === activeType;
        return (
          <button
            key={type}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(type)}
            className={
              `${TAB_RAIL_CLASSES.tab} ` +
              (isActive
                ? `border-warm-400 ${TAB_RAIL_CLASSES.active}`
                : TAB_RAIL_CLASSES.inactive)
            }
          >
            {LOOKUP_TYPE_URL_TO_LABEL[type]}
          </button>
        );
      })}
    </div>
  );
}
